/**
 * デバイス履歴グラフ（KPI 別の小さい LineChart を縦に並べる）
 *
 * 範囲（30m / 3h / 1d / 1w / 1m）切替付き。
 * Recharts を使用（DESIGN.md §5.9 / nx-live と同じスタック）。
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { type HistoryRange, type HistoryRow, getHistory } from "../lib/api.ts";

const RANGES: HistoryRange[] = ["30m", "3h", "1d", "1w", "1m", "1y"];

function fmtTick(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("ja-JP", {
    timeZone: "Asia/Tokyo",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function HistoryChart({ mac }: { mac: string }) {
  const { t } = useTranslation();
  const [range, setRange] = useState<HistoryRange>("1d");
  const [rows, setRows] = useState<HistoryRow[] | null>(null);
  const [source, setSource] = useState<"raw" | "daily_summary">("raw");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const res = await getHistory(mac, range);
        if (cancelled) return;
        setSource(res.source);
        if (res.source === "daily_summary") {
          // 日次サマリーを HistoryRow に合わせて変換（電圧と SoC のみ意味あり、W系は欠落）
          const synthesized: HistoryRow[] = (res.rows as unknown as Array<Record<string, unknown>>).map((r) => ({
            timestamp: typeof r.day === "string" ? `${r.day}T00:00:00+09:00` : null,
            soc_bms_pct: (r.soc_avg_pct as number) ?? null,
            soc_ve_pct: null,
            voltage_v: (r.voltage_avg_v as number) ?? null,
            input_w: (r.energy_in_wh as number) ?? null,
            output_w: r.energy_out_wh != null ? -(r.energy_out_wh as number) : null,
            net_w: null,
            cell_v_min: null,
            cell_v_max: null,
            cell_v_avg: null,
            temp_cell_1_c: null,
            temp_cell_2_c: null,
            temp_inverter_c: null,
          }));
          setRows(synthesized);
        } else {
          setRows(res.rows);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mac, range]);

  const data = useMemo(
    () =>
      (rows || []).map((r, i) => ({
        i,
        ts: r.timestamp ?? "",
        soc_bms: r.soc_bms_pct,
        soc_ve: r.soc_ve_pct,
        v: r.voltage_v,
        net_w: r.net_w,
        input_w: r.input_w,
        output_w: r.output_w !== null ? -r.output_w : null,
        cell_avg: r.cell_v_avg,
        cell_min: r.cell_v_min,
        cell_max: r.cell_v_max,
        temp_inv: r.temp_inverter_c,
        temp_c1: r.temp_cell_1_c,
        temp_c2: r.temp_cell_2_c,
      })),
    [rows],
  );

  return (
    <section className="nx-history">
      <div className="nx-history-header">
        <p className="nx-label">{t("device_detail.history")}</p>
        <div className="nx-history-ranges">
          {RANGES.map((r) => (
            <button
              key={r}
              type="button"
              className={r === range ? "is-active" : ""}
              onClick={() => setRange(r)}
              disabled={loading && r === range}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {loading && <p>{t("common.loading")}</p>}
      {error && <p className="nx-error">{error}</p>}
      {source === "daily_summary" && data.length > 0 && (
        <p className="nx-text-secondary" style={{ fontSize: "0.85rem" }}>
          {t("device_detail.summary_note")}
        </p>
      )}
      {!loading && !error && data.length === 0 && (
        <p className="nx-text-secondary">{t("device_detail.no_history")}</p>
      )}

      {!loading && !error && data.length > 0 && (
        <div className="nx-charts">
          <ChartCard title={t("device_detail.chart_soc")} unit="%">
            <LineChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="ts" tick={{ fontSize: 9 }} interval="preserveStartEnd" tickFormatter={fmtTick} />
              <YAxis domain={[0, 100]} width={36} tick={{ fontSize: 10 }} />
              <Tooltip labelFormatter={(v) => fmtTick(String(v))} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="soc_bms" name="SoC BMS" stroke="var(--primary)" dot={false} strokeWidth={2} connectNulls />
              <Line type="monotone" dataKey="soc_ve" name="SoC VE" stroke="var(--primary-dark)" dot={false} strokeWidth={2} connectNulls />
            </LineChart>
          </ChartCard>

          <ChartCard title={t("device_detail.chart_cell_v")} unit="V">
            <LineChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="ts" tick={{ fontSize: 9 }} interval="preserveStartEnd" tickFormatter={fmtTick} />
              <YAxis width={40} tick={{ fontSize: 10 }} />
              <Tooltip labelFormatter={(v) => fmtTick(String(v))} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="cell_min" name="Min" stroke="var(--primary)" dot={false} strokeWidth={2} connectNulls />
              <Line type="monotone" dataKey="cell_max" name="Max" stroke="var(--danger)" dot={false} strokeWidth={2} connectNulls />
              <Line type="monotone" dataKey="cell_avg" name="Avg" stroke="var(--warning)" dot={false} strokeWidth={2} connectNulls />
            </LineChart>
          </ChartCard>

          <ChartCard title={t("device_detail.chart_power")} unit="W">
            <LineChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="ts" tick={{ fontSize: 9 }} interval="preserveStartEnd" tickFormatter={fmtTick} />
              <YAxis width={40} tick={{ fontSize: 10 }} />
              <Tooltip labelFormatter={(v) => fmtTick(String(v))} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="input_w" name="Input W" stroke="var(--primary)" dot={false} strokeWidth={2} connectNulls />
              <Line type="monotone" dataKey="output_w" name="Output W" stroke="var(--danger)" dot={false} strokeWidth={2} connectNulls />
              <Line type="monotone" dataKey="net_w" name="Net W" stroke="var(--text)" dot={false} strokeWidth={2} connectNulls />
            </LineChart>
          </ChartCard>

          <ChartCard title={t("device_detail.chart_temp")} unit="°C">
            <LineChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="ts" tick={{ fontSize: 9 }} interval="preserveStartEnd" tickFormatter={fmtTick} />
              <YAxis width={40} tick={{ fontSize: 10 }} />
              <Tooltip labelFormatter={(v) => fmtTick(String(v))} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="temp_c1" name="Cell 1" stroke="var(--danger)" dot={false} strokeWidth={2} connectNulls />
              <Line type="monotone" dataKey="temp_c2" name="Cell 2" stroke="var(--primary-dark)" dot={false} strokeWidth={2} connectNulls />
              <Line type="monotone" dataKey="temp_inv" name="Inverter" stroke="var(--primary)" dot={false} strokeWidth={2} connectNulls />
            </LineChart>
          </ChartCard>
        </div>
      )}
    </section>
  );
}

function ChartCard({
  title,
  unit,
  children,
}: {
  title: string;
  unit?: string;
  children: React.ReactElement;
}) {
  return (
    <div className="nx-chart-card">
      <div className="nx-chart-head">
        <span className="nx-chart-title">{title}</span>
        {unit && <span className="nx-chart-unit">{unit}</span>}
      </div>
      <div className="nx-chart-body">
        <ResponsiveContainer width="100%" height={180}>
          {children}
        </ResponsiveContainer>
      </div>
    </div>
  );
}
